import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { LABELS, type CycleState } from "@match/shared";
import { PageHeader, EmptyState } from "@/components/Layout";
import { SessionStateLabel, PHASE_LABEL } from "@/components/session";
import { Button, Dash, ErrorNote } from "@/components/ui";
import { errorMessage } from "@/lib/api";
import { date, money, signClass } from "@/lib/format";
import { useCycle, useCycles } from "@/lib/queries";

/*
 * Cicli operativi.
 *
 * Il ciclo è l'unità economica del modello: nasce con la fee, attraversa le
 * fasi e chiude alla bruciatura del conto prop. Fin qui lo si vedeva solo di
 * sbieco — dentro la scheda di un cliente, o come riga di una fase — e per
 * sapere a che punto fosse bisognava sapere di chi era.
 *
 * Qui non si calcola niente: sono le colonne del ciclo, nella valuta del conto
 * prop. «Fasi» resta la pagina di chi opera; questa è la pagina di chi guarda
 * come stanno andando i cicli.
 */

const TABS = [
  { key: "vivi", label: "Attivi", states: ["ACTIVE"] },
  { key: "chiusi", label: "Chiusi", states: ["CLOSED_PROFIT", "CLOSED_LOSS", "ABANDONED"] },
  { key: "tutti", label: "Tutti", states: [] as string[] },
] as const;

export function Cycles() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = TABS.find((t) => t.key === params.get("stato")) ?? TABS[0];
  const cycles = useCycles({ state: [...tab.states] });

  return (
    <>
      <PageHeader title="Cicli operativi" subtitle="Un ciclo per cliente: fee, fasi, recover, e come è finito" />

      <div className="border-b border-line px-6">
        <nav className="flex gap-1" aria-label="Stato dei cicli">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => {
                const next = new URLSearchParams(params);
                if (t.key === "vivi") next.delete("stato");
                else next.set("stato", t.key);
                setParams(next, { replace: true });
              }}
              aria-current={t.key === tab.key ? "page" : undefined}
              className={[
                "-mb-px border-b-2 px-3 py-2.5 text-sm transition-colors",
                t.key === tab.key ? "border-ink font-medium text-ink" : "border-transparent text-muted hover:text-ink",
              ].join(" ")}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {cycles.isLoading ? (
        <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>
      ) : cycles.error ? (
        <div className="px-6 py-6">
          <ErrorNote>{errorMessage(cycles.error)}</ErrorNote>
        </div>
      ) : !cycles.data?.length ? (
        <EmptyState
          title={tab.key === "vivi" ? "Nessun ciclo attivo" : "Nessun ciclo"}
          hint="Un ciclo nasce con la prima fase di un cliente: aprila da Fasi, e comparirà qui con la sua fee e il suo recover."
          action={
            <Link to="/fasi/nuova" className="inline-flex rounded bg-ink px-3 py-1.5 text-sm font-medium text-page hover:bg-ink/90">
              Nuova fase
            </Link>
          }
        />
      ) : (
        <div className="overflow-x-auto px-6 py-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs text-faint">
                <th className="py-2 pr-4 font-medium">Cliente</th>
                <th className="py-2 pr-4 font-medium">Prop firm</th>
                <th className="py-2 pr-4 font-medium">Fase</th>
                <th className="py-2 pr-4 font-medium">Stato</th>
                <th className="py-2 pr-4 text-right font-medium">{LABELS.terms.recoverAmount}</th>
                <th className="py-2 pr-4 text-right font-medium">{LABELS.terms.feesPaid}</th>
                <th className="py-2 pr-4 text-right font-medium">{LABELS.terms.realRealizedPnl}</th>
                <th className="py-2 pr-4 text-right font-medium">Giorni</th>
                <th className="py-2 font-medium">
                  <span className="sr-only">Azioni</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {cycles.data.map((c) => (
                <tr key={c.id} className="border-b border-line/60 hover:bg-raised/40">
                  <td className="py-2.5 pr-4">
                    <Link to={`/cicli/${c.id}`} className="font-medium text-ink hover:underline">
                      {c.client.name}
                    </Link>
                    {c.label && <span className="ml-2 text-muted">{c.label}</span>}
                  </td>
                  <td className="py-2.5 pr-4">
                    <span className="text-ink">{c.propFirm.name}</span>{" "}
                    <span className="text-muted">{money(c.accountSize, c.currency)}</span>
                  </td>
                  <td className="py-2.5 pr-4 text-muted">{PHASE_LABEL(c.currentPhase)}</td>
                  <td className="py-2.5 pr-4">
                    <CycleStateLabel state={c.state} />
                  </td>
                  <td className="py-2.5 pr-4 text-right text-ink">{money(c.recoverAmount, c.currency)}</td>
                  <td className="py-2.5 pr-4 text-right text-muted">{money(c.feesPaid, c.currency)}</td>
                  <td className={`py-2.5 pr-4 text-right ${signClass(Number(c.realRealizedPnl))}`}>{money(c.realRealizedPnl, c.currency)}</td>
                  <td className="py-2.5 pr-4 text-right text-muted" title={`Aperto il ${date(c.openedAt)}${c.closedAt ? `, chiuso il ${date(c.closedAt)}` : ""}`}>
                    {c.daysOpen}
                  </td>
                  <td className="py-2.5">
                    <div className="flex justify-end">
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/cicli/${c.id}`)}>
                        Apri
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/** FAILED non è un guasto e CLOSED_PROFIT è l'esito che si cerca: i colori lo dicono. */
function CycleStateLabel({ state }: { state: CycleState }) {
  const tone =
    state === "ACTIVE" ? "text-ink" : state === "CLOSED_PROFIT" ? "text-gain" : state === "CLOSED_LOSS" ? "text-loss" : "text-muted";
  return <span className={tone}>{LABELS.cycleState[state]}</span>;
}

// ---------------------------------------------------------------------------

export function CycleDetail() {
  const { id = "" } = useParams();
  const cycle = useCycle(id);

  if (cycle.isLoading) return <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>;
  if (cycle.error || !cycle.data) {
    return (
      <div className="px-6 py-6">
        <ErrorNote>{cycle.error ? errorMessage(cycle.error) : "Ciclo non trovato"}</ErrorNote>
        <Link to="/cicli" className="mt-3 inline-block text-sm text-muted hover:text-ink">
          Torna ai cicli
        </Link>
      </div>
    );
  }

  const c = cycle.data;
  const cur = c.currency;

  return (
    <>
      <PageHeader
        title={c.label ?? `${c.propFirm.name} ${c.preset.name}`}
        subtitle={`${c.client.name} · ${c.propFirm.name} ${money(c.accountSize, cur)} · ${PHASE_LABEL(c.currentPhase)}`}
        actions={
          <>
            <CycleStateLabel state={c.state} />
            <Link to={`/clienti/${c.client.id}`} className="text-sm text-muted hover:text-ink hover:underline">
              Scheda cliente
            </Link>
          </>
        }
      />

      <div className="space-y-6 px-6 py-5">
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Box label={LABELS.terms.recoverAmount} value={money(c.recoverAmount, cur)} hint="Quanto serve recuperare sul conto reale" />
          <Box label={LABELS.terms.feesPaid} value={money(c.feesPaid, cur)} hint="Il prezzo d'acquisto del ciclo" />
          <Box
            label={LABELS.terms.realRealizedPnl}
            value={money(c.realRealizedPnl, cur)}
            tone={signClass(Number(c.realRealizedPnl))}
            hint="Negativo dopo una fase superata, positivo dopo la bruciatura"
          />
          <Box
            label={c.closedAt ? "Durata" : "Aperto da"}
            value={`${c.daysOpen} ${c.daysOpen === 1 ? "giorno" : "giorni"}`}
            hint={`Dal ${date(c.openedAt)}${c.closedAt ? ` al ${date(c.closedAt)}` : ""}`}
          />
        </section>

        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Box label={LABELS.terms.costsRecovered} value={money(c.payoutsReceived, cur)} hint="Payout incassati dalla prop firm" />
          <Box label={LABELS.terms.adjustments} value={money(c.adjustments, cur)} />
          <Box label="Netto voluto" value={money(c.desiredNetProfit, cur)} />
          <Box
            label="CID del ciclo"
            value={c.computerId ? <span className="machine-id break-all text-sm">{c.computerId}</span> : <Dash />}
            hint="Congelato all'apertura: i conti prop di questo ciclo si presentano così"
          />
        </section>

        <section>
          <h2 className="mb-2 text-sm font-medium text-ink">Fasi del ciclo</h2>
          {c.sessions.length === 0 ? (
            <p className="card px-4 py-4 text-sm text-muted">
              Nessuna fase: il ciclo esiste ma non è ancora andato a mercato.
            </p>
          ) : (
            <div className="card divide-y divide-line">
              {c.sessions.map((s) => (
                <div key={s.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm">
                  <span className="font-medium text-ink">{PHASE_LABEL(s.phase)}</span>
                  <SessionStateLabel state={s.state} />
                  <span className="text-muted">{s.canonicalSymbol}</span>
                  <span className="text-prop">{s.propAccount.label}</span>
                  <span className="text-broker">{s.brokerAccount.label}</span>
                  <span className="text-muted">
                    {s.pairs} {s.pairs === 1 ? "operazione" : "operazioni"}
                  </span>
                  <span className="text-muted" title="Da recuperare all'inizio della fase">
                    obiettivo {money(s.profitTarget, cur)}
                  </span>
                  <span className={signClass(Number(s.costsRecovered))} title={LABELS.terms.costsRecovered}>
                    {money(s.costsRecovered, s.brokerAccount.currency)}
                  </span>
                  <span className="text-faint">
                    {date(s.startedAt)}
                    {s.completedAt ? ` → ${date(s.completedAt)}` : ""}
                  </span>
                  <Link to={`/fasi/${s.id}`} className="ml-auto text-muted hover:text-ink hover:underline">
                    Apri la fase
                  </Link>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </>
  );
}

function Box({ label, value, hint, tone }: { label: string; value: React.ReactNode; hint?: string; tone?: string }) {
  return (
    <div className="card p-4">
      <p className="text-xs text-faint">{label}</p>
      <p className={`mt-1 text-lg font-medium ${tone ?? "text-ink"}`}>{value}</p>
      {hint && <p className="mt-1 text-xs text-faint">{hint}</p>}
    </div>
  );
}
